import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { Button, Badge } from '@vnetwork/ui/primitives';
import {
  stepRow,
  stepRowUpcoming,
  markerDone,
  markerCurrent,
  markerUpcoming,
  stepTitle,
  stepDesc,
} from './onboarding-styles';

export type JourneyStepState = 'done' | 'current' | 'upcoming';

export interface JourneyStepRowProps {
  index: number;
  title: string;
  description: string;
  state?: JourneyStepState;
  /** Leading icon (lucide) shown next to the title. */
  icon?: ReactNode;
  /** Primary action for an actionable step (rendered when `current`). */
  actionLabel?: string;
  onAction?: () => void;
}

const MARKER = { done: markerDone, current: markerCurrent, upcoming: markerUpcoming };

/**
 * One row of the journey timeline. Renders a state-aware marker (✓ done · solid
 * current · outlined upcoming), an icon + title + description, and — when current
 * and actionable — a CTA. The connector line + hover lift come from
 * `onboarding-css.tsx` via the `vn-ob-*` classes.
 */
export function JourneyStepRow({
  index,
  title,
  description,
  state = 'upcoming',
  icon,
  actionLabel,
  onAction,
}: JourneyStepRowProps): ReactNode {
  const done = state === 'done';
  const actionable = state === 'current' && !!actionLabel && !!onAction;

  return (
    <div
      className={`vn-ob-step ${done ? 'is-done' : ''} vn-ob-row ${actionable ? 'is-actionable' : ''}`}
      style={state === 'upcoming' ? stepRowUpcoming : stepRow}
    >
      <span className="vn-ob-marker" style={MARKER[state]} aria-hidden>
        {done ? <Check size={17} strokeWidth={3} /> : index}
      </span>

      <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 'var(--vn-space-1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          {icon && <span style={{ color: 'var(--vn-fg-muted)', display: 'inline-flex' }}>{icon}</span>}
          <p style={stepTitle}>{title}</p>
          {done && (
            <Badge tone="success" appearance="subtle" size="sm">
              Done
            </Badge>
          )}
          {state === 'current' && (
            <Badge tone="primary" appearance="subtle" size="sm">
              Next
            </Badge>
          )}
        </div>
        <p style={stepDesc}>{description}</p>
      </div>

      {actionable && (
        <Button size="sm" onClick={onAction} style={{ flexShrink: 0, alignSelf: 'center' }}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
}
